import { normalizeSiteUserRole } from '../data/siteUsers';
import { getEnvConfig } from '../utils/env';
import type { SiteAuthRuntime, SiteUserRole, SiteUserSession } from '../types';

declare global {
  interface Window {
    __BLISS_SITE_PREVIEW_BASE__?: string;
  }
}

interface BlissBootstrap {
  projectId: string;
  clientId?: string;
  siteState?: string;
}

interface BlissAppConfigResponse {
  allowed_roles?: string[];
  role_source?: string;
  default_role?: string;
}

interface BlissAppUser {
  id: string;
  email: string;
  name?: string | null;
  role: string;
  company_id?: string | null;
}

interface RuntimeContext {
  bootstrap: BlissBootstrap;
  appBaseUrl: string;
}

const TOKEN_KEY = 'victorias-place-site-token';
const USER_KEY = 'victorias-place-site-user';
const DEFAULT_BLISS_PROJECT_ID = 'a5679493-2358-4bf1-ad8e-f5547ff204ec';
const DEFAULT_BLISS_API_BASE_URL = 'https://blissits.com';

let runtimeContextCache: RuntimeContext | null = null;
let runtimeConfigCache: SiteAuthRuntime | null = null;

function normalizeBaseUrl(value: string): string {
  return value.trim().replace(/\/+$/, '');
}

function getErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') {
    return fallback;
  }

  const source = payload as {
    error?: string | { message?: string };
    message?: string;
  };

  if (typeof source.error === 'string' && source.error) {
    return source.error;
  }

  if (source.error && typeof source.error === 'object' && typeof source.error.message === 'string') {
    return source.error.message;
  }

  if (typeof source.message === 'string' && source.message) {
    return source.message;
  }

  return fallback;
}

function normalizeSession(user: BlissAppUser, fallbackRole: SiteUserRole): SiteUserSession {
  const launchRole = user.role === 'site_owner' ? 'admin' : user.role;
  const normalizedRole = normalizeSiteUserRole(launchRole, fallbackRole);
  const derivedName = user.email.includes('@') ? user.email.split('@')[0] : user.email;

  return {
    id: user.id,
    name: user.name?.trim() || derivedName,
    email: user.email,
    role: normalizedRole,
    companyId: user.company_id ?? null,
  };
}

function readStoredJson<T>(key: string): T | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeStoredJson(key: string, value: unknown) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(key, JSON.stringify(value));
}

export function getPreviewBase(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  return typeof window.__BLISS_SITE_PREVIEW_BASE__ === 'string'
    ? window.__BLISS_SITE_PREVIEW_BASE__
    : '';
}

export function getStoredToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(TOKEN_KEY);
}

export function getStoredSession(): SiteUserSession | null {
  return readStoredJson<SiteUserSession>(USER_KEY);
}

export function clearStoredSession() {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(USER_KEY);
}

function storeSession(token: string, session: SiteUserSession) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(TOKEN_KEY, token);
  writeStoredJson(USER_KEY, session);
}

async function readResponseJson(response: Response): Promise<unknown> {
  return response.json().catch(() => null);
}

async function resolveRuntimeContext(): Promise<RuntimeContext> {
  if (runtimeContextCache) {
    return runtimeContextCache;
  }

  const env = getEnvConfig();
  const configuredProjectId = env.BLISS_PROJECT_ID?.trim() || DEFAULT_BLISS_PROJECT_ID;
  const configuredApiBase = env.BLISS_API_BASE_URL?.trim() || DEFAULT_BLISS_API_BASE_URL;

  if (configuredProjectId && configuredApiBase) {
    runtimeContextCache = {
      bootstrap: {
        projectId: configuredProjectId,
        siteState: 'live',
      },
      appBaseUrl: `${normalizeBaseUrl(configuredApiBase)}/api/projects/${encodeURIComponent(configuredProjectId)}/app`,
    };

    return runtimeContextCache;
  }

  const response = await fetch('/api/site/bootstrap', { credentials: 'include' });
  const payload = await readResponseJson(response);

  if (!response.ok) {
    throw new Error(
      getErrorMessage(
        payload,
        'Failed to resolve Bliss site context. This portal must run inside a Bliss site preview or live hosted domain.',
      ),
    );
  }

  const bootstrap = ((payload as { data?: BlissBootstrap })?.data ?? payload) as BlissBootstrap;

  if (!bootstrap?.projectId) {
    throw new Error('Missing Bliss project ID from site bootstrap.');
  }

  runtimeContextCache = {
    bootstrap,
    appBaseUrl: `/api/projects/${encodeURIComponent(bootstrap.projectId)}/app`,
  };

  return runtimeContextCache;
}

export async function getSiteAuthRuntime(): Promise<SiteAuthRuntime> {
  if (runtimeConfigCache) {
    return runtimeConfigCache;
  }

  const runtimeContext = await resolveRuntimeContext();
  const response = await fetch(`${runtimeContext.appBaseUrl}/config`);
  const payload = await readResponseJson(response);

  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'Failed to load Bliss app config.'));
  }

  const config = ((payload as { data?: BlissAppConfigResponse })?.data ?? payload) as BlissAppConfigResponse;
  const allowedRoles = Array.isArray(config.allowed_roles)
    ? config.allowed_roles.map((role) => normalizeSiteUserRole(role)).filter((role, index, roles) => roles.indexOf(role) === index)
    : [];

  const defaultRole = normalizeSiteUserRole(
    config.default_role,
    allowedRoles.includes('read_only') ? 'read_only' : (allowedRoles[0] ?? 'read_only'),
  );

  runtimeConfigCache = {
    projectId: runtimeContext.bootstrap.projectId,
    siteState: runtimeContext.bootstrap.siteState,
    allowedRoles: allowedRoles.length ? allowedRoles : [defaultRole],
    roleSource: config.role_source ?? 'bootstrap',
    defaultRole,
    apiBaseUrl: runtimeContext.appBaseUrl,
  };

  return runtimeConfigCache;
}

export async function signInWithBliss(email: string, password: string): Promise<SiteUserSession> {
  const runtime = await getSiteAuthRuntime();
  const response = await fetch(`${runtime.apiBaseUrl}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: email.trim(),
      password,
    }),
  });

  const payload = await readResponseJson(response);

  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'Unable to sign in right now.'));
  }

  const result = payload as { token?: string; user?: BlissAppUser };
  if (!result.token || !result.user?.id) {
    throw new Error('Bliss sign-in did not return a valid session.');
  }

  const session = normalizeSession(result.user, runtime.defaultRole);
  storeSession(result.token, session);
  return session;
}

export async function signInWithBlissLaunchToken(launchToken: string): Promise<SiteUserSession> {
  const runtimeContext = await resolveRuntimeContext();
  const runtime = await getSiteAuthRuntime();
  const response = await fetch(`${runtimeContext.appBaseUrl}/auth/launch/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      token: launchToken,
    }),
  });

  const payload = await readResponseJson(response);

  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'Unable to open the Victoria\'s Place portal session.'));
  }

  const result = ((payload as { data?: unknown })?.data ?? payload) as { token?: string; user?: BlissAppUser };
  if (!result.token || !result.user?.id) {
    throw new Error('Bliss launch did not return a valid portal session.');
  }

  const session = normalizeSession(result.user, runtime.defaultRole);
  storeSession(result.token, session);
  return session;
}

export async function registerWithBliss(input: {
  email: string;
  password: string;
  name?: string;
  role: 'vendor' | 'resource_vendor' | 'adult_model' | 'parent' | 'donor';
}): Promise<SiteUserSession> {
  const runtime = await getSiteAuthRuntime();
  const response = await fetch(`${runtime.apiBaseUrl}/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: input.email.trim(),
      password: input.password,
      name: input.name?.trim() || '',
      role: input.role,
    }),
  });

  const payload = await readResponseJson(response);

  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'Unable to create a vendor portal account right now.'));
  }

  const result = payload as { token?: string; user?: BlissAppUser };
  if (!result.token || !result.user?.id) {
    throw new Error('Bliss sign-up did not return a valid session.');
  }

  const session = normalizeSession(result.user, runtime.defaultRole);
  storeSession(result.token, session);
  return session;
}

export async function getCurrentBlissSession(): Promise<SiteUserSession | null> {
  const token = getStoredToken();
  if (!token) {
    return null;
  }

  const runtime = await getSiteAuthRuntime();
  const response = await fetch(`${runtime.apiBaseUrl}/auth/me`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401) {
    clearStoredSession();
    return null;
  }

  const payload = await readResponseJson(response);

  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'Unable to verify the current site-user session.'));
  }

  const result = payload as { user?: BlissAppUser; data?: { user?: BlissAppUser } };
  const user = result.user ?? result.data?.user;
  if (!user?.id) {
    clearStoredSession();
    return null;
  }

  const session = normalizeSession(user, runtime.defaultRole);
  storeSession(token, session);
  return session;
}

export async function changeBlissPassword(currentPassword: string, newPassword: string): Promise<void> {
  const token = getStoredToken();
  if (!token) {
    throw new Error('You need to sign in again before changing the password.');
  }

  const runtime = await getSiteAuthRuntime();
  const response = await fetch(`${runtime.apiBaseUrl}/auth/change-password`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });

  const payload = await readResponseJson(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'Unable to update the password right now.'));
  }
}
